import React from 'react';
import { motion } from 'framer-motion';
import { Search, Sliders, HeartHandshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const steps = [{
  icon: Search,
  number: '01',
  title: 'Antes de tocarte, te evalúa.',
  description: "Analiza tu tipo de piel, tu historia, tus hábitos, tus preocupaciones específicas. Construye un mapa de lo que tu piel necesita — no de lo que funciona 'en general.'"
}, {
  icon: Sliders,
  number: '02',
  title: 'Luego diseña tu protocolo.',
  description: 'Combinando técnicas avanzadas de cuidado coreano con tecnología de última generación disponible en su spa, Kate selecciona el tratamiento exacto para tu condición. No más. No menos.'
}, {
  icon: HeartHandshake,
  number: '03',
  title: 'Y te acompaña en el proceso.',
  description: 'No te vende una sesión y desaparece. Te guía. Te educa. Te enseña a entender tu propia piel para que los resultados duren mucho más allá de la sala de tratamiento.'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 18
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};
export function ProtocolSection() {
  const {
    t
  } = useLanguage();
  const steps = [{
    icon: Search,
    number: '01',
    title: t('protocol.s1_title'),
    description: t('protocol.s1_desc')
  }, {
    icon: Sliders,
    number: '02',
    title: t('protocol.s2_title'),
    description: t('protocol.s2_desc')
  }, {
    icon: HeartHandshake,
    number: '03',
    title: t('protocol.s3_title'),
    description: t('protocol.s3_desc')
  }];
  return <section id="protocolo" className="py-12 md:py-24 bg-brand-cream relative" data-id="element-366" data-mp="125h9t8j97dtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367" data-mp="38wgtibgp7a0k">
        <div className="relative text-center max-w-4xl mx-auto mb-12 md:mb-20" data-id="element-368" data-mp="33wknk6ofx8id">
          {/* Soft gold halo behind the heading */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-[420px] w-[720px] max-w-[130%] rounded-full opacity-70" style={{
          background: 'radial-gradient(closest-side, rgba(184,134,11,0.13), rgba(184,134,11,0.04) 55%, transparent 72%)'
        }} data-id="element-369" data-mp="17u7pdv7ggdvg" />

          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.25
        }} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.11,
              delayChildren: 0.05
            }
          }
        }} data-id="element-370" data-mp="1qstudl63ep4n">
            <motion.p variants={fadeUp} className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-brand-gold font-medium mb-5" data-id="element-371" data-mp="29gp0qjzdl0nq">
              {t('protocol.eyebrow')}
            </motion.p>

            <motion.h2 variants={fadeUp} className="text-[28px] sm:text-4xl md:text-5xl font-serif text-brand-brown leading-[1.15] max-w-3xl mx-auto" data-id="element-372" data-mp="2jm73qg75aupm">
              {t('protocol.title')}
            </motion.h2>

            {/* Fine separator with a gold diamond */}
            <motion.div variants={fadeUp} aria-hidden="true" className="flex items-center justify-center gap-3 my-7 md:my-9" data-id="element-373" data-mp="37azrhpdm5w95">
              <span className="block h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-brand-gold/60" data-id="element-374" data-mp="2xlpy8671a7jg" />
              <span className="block h-1.5 w-1.5 rotate-45 bg-brand-gold" data-id="element-375" data-mp="2tlfuz1amlv8a" />
              <span className="block h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-brand-gold/60" data-id="element-376" data-mp="3q2afnyryfuxc" />
            </motion.div>

            <motion.p variants={fadeUp} className="text-base md:text-lg text-brand-brown-soft leading-relaxed max-w-[34ch] mx-auto" data-id="element-377" data-mp="3larbq7uq48vl">
              {t('protocol.p1')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-5 text-2xl sm:text-3xl md:text-[2.5rem] font-serif italic text-brand-brown leading-snug" data-id="element-378" data-mp="3au71m8757xfr">
              {t('protocol.p2')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-6 text-base md:text-lg text-brand-charcoal/85 leading-[1.9] max-w-[56ch] mx-auto" data-id="element-379" data-mp="3ojsb8rz1z0b4">
              {t('protocol.p3')}
            </motion.p>

            <motion.p variants={fadeUp} className="mt-12 md:mt-16 text-[11px] md:text-xs uppercase tracking-[0.18em] text-brand-brown-soft" data-id="element-380" data-mp="3fr77oj3zt06v">
              {t('protocol.approach_intro')}
            </motion.p>

            {/* Four-step method — horizontal rows framed by gold diamonds */}
            <div className="relative mt-8 md:mt-10 max-w-3xl mx-auto space-y-3.5" data-id="element-381" data-mp="g6ldza0z5ss4">
              {['a1', 'a2', 'a3', 'a4'].map((key, i) => <motion.div key={key} variants={fadeUp} className="group flex items-start gap-3 sm:gap-4" data-id="element-382" data-mp="1vwkx7zrqbx19" data-mpi={i}>
                  <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-brand-gold/70 transition-all duration-500 group-hover:bg-brand-gold group-hover:scale-125" data-id="element-383" data-mp="25lw37djbklkx" />
                  <span aria-hidden="true" className="mt-[10px] block h-px w-4 sm:w-6 flex-shrink-0 bg-brand-gold/50 transition-all duration-500 group-hover:w-8 sm:group-hover:w-10 group-hover:bg-brand-gold/80" data-id="element-384" data-mp="1cyi1z38l93v0" />

                  <div className="relative flex flex-1 items-start gap-3 sm:gap-4" data-id="element-385" data-mp="338l8pxd8dgbj">
                    <span className="w-7 flex-shrink-0 font-serif text-[13px] tracking-[0.2em] text-brand-gold leading-[1.6]" data-id="element-386" data-mp="2yata6ki2yvyd">
                      {`0${i + 1}`}
                    </span>
                    <p className="text-left text-[13.5px] md:text-sm leading-[1.6] text-brand-charcoal/90" data-id="element-387" data-mp="3acb1nhrpninw">
                      {t(`protocol.${key}`)}
                    </p>
                    {/* Fine gold rule filling the remaining space */}
                    <span aria-hidden="true" className="hidden sm:block mt-[10px] flex-1 h-px bg-gradient-to-r from-brand-gold/45 to-brand-gold/10 transition-all duration-500 group-hover:from-brand-gold group-hover:to-brand-gold/30" data-id="element-388" data-mp="3ka8p431qxbfg" />
                  </div>

                  <span aria-hidden="true" className="hidden sm:block mt-[10px] h-px w-6 flex-shrink-0 bg-brand-gold/50 transition-all duration-500 group-hover:w-10 group-hover:bg-brand-gold/80" data-id="element-389" data-mp="2wj3cdwr3qz9d" />
                  <span aria-hidden="true" className="hidden sm:block mt-[7px] h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-brand-gold/70 transition-all duration-500 group-hover:bg-brand-gold group-hover:scale-125" data-id="element-390" data-mp="28uk1rakkyae4" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Premium framed portrait */}
        <motion.div className="group relative max-w-3xl mx-auto mb-12 md:mb-20" initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-391" data-mp="bzwm1gqq2hnn">
          <div className="absolute inset-0 bg-brand-gold/10 translate-x-4 translate-y-4 rounded-sm transition-transform duration-700 group-hover:translate-x-6 group-hover:translate-y-6" data-id="element-392" data-mp="3kfgdca7u1q02"></div>
          <div className="absolute inset-0 border border-brand-gold/40 -translate-x-4 -translate-y-4 rounded-sm transition-transform duration-700 group-hover:-translate-x-6 group-hover:-translate-y-6" data-id="element-393" data-mp="185yasfue0nzt"></div>

          <div className="relative z-10 overflow-hidden rounded-sm border border-brand-gold/30 shadow-xl bg-brand-dark" data-id="element-394" data-mp="y6z5964x58h1">
            <img src="https://cdn.magicpatterns.com/uploads/nnCMRw8mvAvPahteyMdWUW/EL_PROTOCOLO_PIEL_SANA,NO_PERFECTA.png" alt="Kate en su cabina de tratamiento en Tairu Aesthetics" loading="lazy" className="w-full max-h-[560px] md:max-h-none object-cover object-top transition-transform duration-[1400ms] ease-out group-hover:scale-105" data-id="element-395" data-mp="135acd93tx4vk" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" data-id="element-396" data-mp="kgvq0z4gif13"></div>
            <div className="absolute bottom-5 left-5 right-5 z-20" data-id="element-397" data-mp="ai6dzmz8rosr">
              <span className="block h-px w-10 bg-brand-gold mb-3 transition-all duration-700 group-hover:w-20" data-id="element-398" data-mp="akk6bo2jemwm"></span>
              <p className="text-glow-white-strong text-white font-serif text-lg md:text-2xl leading-snug" data-id="element-399" data-mp="3j0gkuxc9r0yn">
                {t('protocol.image_caption')}
              </p>
            </div>
            <motion.div className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" initial={{
            x: '-50%',
            opacity: 0
          }} whileInView={{
            x: '450%',
            opacity: 1
          }} viewport={{
            once: true,
            amount: 0.25
          }} transition={{
            duration: 1.7,
            delay: 0.6,
            ease: 'easeInOut'
          }} data-id="element-400" data-mp="shdjc3f9e3z" />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-401" data-mp="olpqev8xmzsi">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} className="bg-white p-8 rounded-sm shadow-sm border border-brand-gold/10 relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-402" data-mp="109c2v5w1fc67" data-mpi={index}>
              <div className="absolute -right-4 -top-4 text-9xl font-serif font-bold text-brand-cream opacity-50 group-hover:scale-110 transition-transform duration-500" data-id="element-403" data-mp="1a4m5nvu9fnl1">
                {step.number}
              </div>

              <div className="relative z-10" data-id="element-404" data-mp="3uxhadqbf2fai">
                <div className="w-14 h-14 bg-brand-gold/10 rounded-full flex items-center justify-center mb-6 text-brand-gold" data-id="element-405" data-mp="3pwjszfy8dpa2">
                  <step.icon className="w-7 h-7" data-id="element-406" data-mp="25gbwpyildc8v" />
                </div>
                <h3 className="text-xl font-serif text-brand-brown mb-4 font-bold" data-id="element-407" data-mp="2adlwo83lx6ay">
                  {step.title}
                </h3>
                <p className="text-brand-charcoal leading-relaxed" data-id="element-408" data-mp="2ee5sn8cg23vi">
                  {step.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}